import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1400&q=80";

/**
 * Premium vehicle gallery with:
 *  - Horizontal swipe on mobile (CSS scroll-snap)
 *  - Prev/Next arrow buttons on desktop
 *  - Dot indicators
 *  - Optional lightbox on click
 *
 * Props:
 *  - images: string[] (falls back to [image_url] if empty)
 *  - image_url: string (legacy single image, used as fallback)
 *  - alt: string (vehicle name)
 *  - aspect: tailwind aspect class, default "aspect-[16/10]"
 *  - rounded: tailwind rounded class, default ""
 *  - enableLightbox: bool, default true
 *  - overlayChildren: ReactNode (badges/labels rendered above image)
 *  - testId: string
 */
export default function VehicleGallery({
  images,
  image_url,
  alt = "Vehicle",
  aspect = "aspect-[16/10]",
  rounded = "",
  enableLightbox = true,
  overlayChildren = null,
  testId,
}) {
  const list = useMemo(() => {
    const arr = (Array.isArray(images) ? images : []).filter(Boolean);
    if (image_url && !arr.includes(image_url)) arr.unshift(image_url);
    return arr.length ? arr : [FALLBACK];
  }, [images, image_url]);

  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const scrollRef = useRef(null);
  const isMulti = list.length > 1;

  // Scroll tracking → update active dot on swipe
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !isMulti) return;
    let raf = null;
    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const idx = Math.round(el.scrollLeft / el.clientWidth);
        setActive((prev) => (prev !== idx ? idx : prev));
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [isMulti]);

  const goTo = useCallback((i) => {
    const el = scrollRef.current;
    if (!el) return;
    const idx = (i + list.length) % list.length;
    el.scrollTo({ left: idx * el.clientWidth, behavior: "smooth" });
    setActive(idx);
  }, [list.length]);

  const prev = useCallback((e) => { e?.stopPropagation?.(); e?.preventDefault?.(); goTo(active - 1); }, [active, goTo]);
  const next = useCallback((e) => { e?.stopPropagation?.(); e?.preventDefault?.(); goTo(active + 1); }, [active, goTo]);

  const openLightbox = (e) => {
    if (!enableLightbox) return;
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setLightbox(true);
  };

  return (
    <>
      <div
        data-testid={testId}
        className={`relative group/gallery overflow-hidden bg-ink-700 ${aspect} ${rounded}`}
      >
        <div
          ref={scrollRef}
          className="flex h-full w-full snap-x snap-mandatory overflow-x-auto scroll-smooth no-scrollbar"
          style={{ scrollbarWidth: "none" }}
        >
          {list.map((src, i) => (
            <div key={`${src}-${i}`} className="relative flex-shrink-0 w-full h-full snap-center">
              <img
                src={src}
                alt={`${alt} — photo ${i + 1}`}
                loading={i === 0 ? "eager" : "lazy"}
                onClick={openLightbox}
                className={`w-full h-full object-cover transition-transform duration-700 ease-out ${enableLightbox ? "cursor-zoom-in" : ""} group-hover/gallery:scale-[1.03]`}
                onError={(e) => { e.currentTarget.src = FALLBACK; }}
                data-testid={testId ? `${testId}-img-${i}` : undefined}
              />
            </div>
          ))}
        </div>

        {/* Gradient overlay to preserve legibility of badges */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/20 to-transparent" />

        {overlayChildren}

        {/* Desktop arrows */}
        {isMulti && (
          <>
            <button
              type="button"
              aria-label="Previous photo"
              onClick={prev}
              data-testid={testId ? `${testId}-prev` : undefined}
              className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 border border-white/15 text-cream items-center justify-center opacity-0 group-hover/gallery:opacity-100 hover:bg-black/80 transition-opacity backdrop-blur"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              aria-label="Next photo"
              onClick={next}
              data-testid={testId ? `${testId}-next` : undefined}
              className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 border border-white/15 text-cream items-center justify-center opacity-0 group-hover/gallery:opacity-100 hover:bg-black/80 transition-opacity backdrop-blur"
            >
              <ChevronRight size={18} />
            </button>
          </>
        )}

        {/* Photo count + zoom hint (top-right corner, only if multi) */}
        {isMulti && (
          <div className="absolute bottom-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/70 border border-white/10 text-[10px] tracking-widest text-white/80">
            {enableLightbox && <ZoomIn size={11} className="text-cream" />}
            <span data-testid={testId ? `${testId}-counter` : undefined}>{active + 1} / {list.length}</span>
          </div>
        )}

        {/* Dot indicators */}
        {isMulti && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
            {list.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to photo ${i + 1}`}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); goTo(i); }}
                data-testid={testId ? `${testId}-dot-${i}` : undefined}
                className={`h-1.5 rounded-full transition-all ${i === active ? "w-6 bg-cream" : "w-1.5 bg-white/40 hover:bg-white/70"}`}
              />
            ))}
          </div>
        )}
      </div>

      {lightbox && (
        <Lightbox
          images={list}
          startIndex={active}
          alt={alt}
          onClose={() => setLightbox(false)}
        />
      )}
    </>
  );
}

function Lightbox({ images, startIndex, alt, onClose }) {
  const [idx, setIdx] = useState(startIndex);

  const prev = useCallback(() => setIdx((i) => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setIdx((i) => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, prev, next]);

  const isMulti = images.length > 1;

  return (
    <div
      data-testid="vehicle-lightbox"
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 sm:p-8 animate-in fade-in"
      onClick={onClose}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        data-testid="vehicle-lightbox-close"
        className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 border border-white/20 text-cream flex items-center justify-center hover:bg-white/20 transition"
      >
        <X size={20} />
      </button>

      {isMulti && (
        <>
          <button
            type="button"
            aria-label="Previous"
            onClick={(e) => { e.stopPropagation(); prev(); }}
            data-testid="vehicle-lightbox-prev"
            className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 border border-white/20 text-cream flex items-center justify-center hover:bg-white/20 transition"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            type="button"
            aria-label="Next"
            onClick={(e) => { e.stopPropagation(); next(); }}
            data-testid="vehicle-lightbox-next"
            className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 border border-white/20 text-cream flex items-center justify-center hover:bg-white/20 transition"
          >
            <ChevronRight size={22} />
          </button>
        </>
      )}

      <img
        src={images[idx]}
        alt={`${alt} — photo ${idx + 1}`}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[85vh] max-w-[92vw] object-contain rounded-xl shadow-2xl"
        onError={(e) => { e.currentTarget.src = FALLBACK; }}
      />

      {isMulti && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-xs text-cream tracking-widest">
          {idx + 1} / {images.length}
        </div>
      )}
    </div>
  );
}
